'use client'

import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  Navigation,
  Text,
  Container,
  Icon,
  Spacer,
  ColorCode,
  Cell,
} from '@vinted/web-ui'
import { InfoCircle16, Redirect16, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { redirectToPageInNewTab } from '@marketplace-web/browser/browser-navigation-util'
import { ClickAdEventTarget, VanPlacementConfigModel } from '@marketplace-web/ads/ads-data'

import AdInfo from '../../AdInfo'
import useAdEventTracking from '../../../../hooks/useAdEventTracking'
import useFetchVanCreative from '../../../../hooks/useFetchVanCreative'
import useClickAdEventTracking from '../../../../hooks/useClickAdEventTracking'
import { VAN_CAMPAIGN_PRIVACY_POLICY_URL, PRIVACY_POLICY_URL } from '../../../../constants/routes'
import DotsIndicator from './DotsIndicator'

type Props = {
  isOpen: boolean
  placements: Array<VanPlacementConfigModel>
  onClose: (target: ClickAdEventTarget) => void
  currentPlacementIndex: number
  onPlacementIndexChange: (index: number) => void
}

const IFRAME_BUTTON_CLOSE_DELAY = 100
const RETRY_INTERVAL_MS = 200
const FADE_DURATION_MS = 100
const MAX_DIALOG_WIDTH = '375px'

const VanMultiOfferContent = ({
  isOpen,
  placements,
  onClose,
  currentPlacementIndex,
  onPlacementIndexChange,
}: Props) => {
  const [isAdRendered, setIsAdRendered] = useState(false)
  const [isIframeVisible, setIsIframeVisible] = useState(true)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const trackedReceivedAdIds = useRef<Set<string>>(new Set())
  const trackedViewAdIds = useRef<Set<string>>(new Set())

  const currentPlacement = placements[currentPlacementIndex]
  const creative = currentPlacement?.creative
  const placementId = currentPlacement?.id

  const shouldTrackReceivedAd = placementId ? !trackedReceivedAdIds.current.has(placementId) : false
  const shouldTrackViewAd = placementId ? !trackedViewAdIds.current.has(placementId) : false

  const handleReceivedAdTracked = () => {
    if (!placementId) return

    trackedReceivedAdIds.current.add(placementId)
  }

  const handleViewAdTracked = () => {
    if (!placementId) return

    trackedViewAdIds.current.add(placementId)
  }

  const { viewableImpressionRef, impressionRef } = useAdEventTracking({
    isAdRendered,
    placementConfig: currentPlacement || ({} as VanPlacementConfigModel),
    customPlacementId: currentPlacement?.placementId,
    shouldTrackReceivedAd,
    shouldTrackViewAd,
    onReceivedAdTracked: handleReceivedAdTracked,
    onViewAdTracked: handleViewAdTracked,
  })
  const { trackClickAdEvent } = useClickAdEventTracking()

  const translate = useTranslate('vinted_ads_network.ad_offer')
  const translateMulti = useTranslate('vinted_ads_network.multiple_ad_offer')

  const { iframeContent } = useFetchVanCreative({
    config: currentPlacement || ({} as VanPlacementConfigModel),
    creative: creative || ({} as NonNullable<VanPlacementConfigModel['creative']>),
  })

  const onOfferButtonClick = () => {
    if (!creative) return

    redirectToPageInNewTab(creative.clickThroughUrl)
    trackClickAdEvent(currentPlacement, ClickAdEventTarget.Cta)
  }

  const onAdInfoClick = () => {
    if (!currentPlacement?.id) return

    trackClickAdEvent(currentPlacement, ClickAdEventTarget.AdInfo)

    redirectToPageInNewTab(VAN_CAMPAIGN_PRIVACY_POLICY_URL(currentPlacement.id))
  }

  const onPrivacyPolicyClick = () => {
    if (!currentPlacement) return

    trackClickAdEvent(currentPlacement, ClickAdEventTarget.PrivacyPolicy)

    redirectToPageInNewTab(PRIVACY_POLICY_URL)
  }

  const onNextOffer = () => {
    if (!currentPlacement || isTransitioning) return

    const nextIndex = currentPlacementIndex >= placements.length - 1 ? 0 : currentPlacementIndex + 1

    trackClickAdEvent(currentPlacement, ClickAdEventTarget.SecondaryButton)
    setIsTransitioning(true)
    setIsIframeVisible(false)

    setTimeout(() => {
      setIsAdRendered(false)
      onPlacementIndexChange(nextIndex)
    }, FADE_DURATION_MS)
  }

  useEffect(() => {
    if (!iframeContent || !isOpen) return undefined

    setIsAdRendered(true)

    let timeoutId: NodeJS.Timeout | null = null
    let iframe: HTMLIFrameElement | null = null

    const handleIframeLoad = () => {
      setTimeout(() => {
        setIsIframeVisible(true)
        setIsTransitioning(false)
      }, 50)
    }

    const checkIframeReady = () => {
      iframe = iframeRef.current

      if (!iframe) {
        timeoutId = setTimeout(checkIframeReady, 10)

        return
      }

      if (iframe.contentDocument?.readyState === 'complete') {
        handleIframeLoad()
      } else {
        iframe.addEventListener('load', handleIframeLoad, { once: true })
      }
    }

    checkIframeReady()

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad)
      }
    }
  }, [iframeContent, isOpen, currentPlacementIndex])

  useEffect(() => {
    if (!iframeContent) return undefined

    let timeoutId: NodeJS.Timeout | null = null

    const clearExistingTimeout = () => {
      if (!timeoutId) return
      clearTimeout(timeoutId)
      timeoutId = null
    }

    const tryAttachListener = () => {
      const iframe = document.getElementById('van-offer-iframe') as HTMLIFrameElement | null

      if (!iframe || !iframe.contentWindow || !currentPlacement) {
        timeoutId = setTimeout(tryAttachListener, RETRY_INTERVAL_MS)

        return
      }

      const iframeImage = iframe.contentWindow.document.getElementById('van-creative')

      if (!iframeImage) {
        timeoutId = setTimeout(tryAttachListener, RETRY_INTERVAL_MS)

        return
      }

      iframeImage.addEventListener('click', () => {
        setTimeout(() => {
          trackClickAdEvent(currentPlacement, ClickAdEventTarget.Creative)
        }, IFRAME_BUTTON_CLOSE_DELAY)
      })

      clearExistingTimeout()
    }

    tryAttachListener()

    return clearExistingTimeout
  }, [iframeContent, trackClickAdEvent, currentPlacementIndex, currentPlacement])

  if (!currentPlacement || !creative) return null

  if (!iframeContent || !isOpen || !currentPlacement.id) return null

  const offerCount = placements.length
  const isMultipleOffers = offerCount > 1
  const subtitleKey = isMultipleOffers ? 'multiple' : 'single'

  return (
    <Dialog
      contentDimensions={{
        maxWidth: MAX_DIALOG_WIDTH,
      }}
      show={isOpen}
      closeOnOverlay
      defaultCallback={() => onClose(ClickAdEventTarget.CloseClickOutsidePlacement)}
      testId="van-multi-offer-content-dialog"
    >
      <div ref={viewableImpressionRef} className="van-multi-offer-content-wrapper">
        <div ref={impressionRef} key={placementId || currentPlacementIndex}>
          <Navigation
            body={translate('header_title')}
            right={
              <Button
                inline
                iconName={X24}
                iconColor={ColorCode.GreyscaleLevel2}
                styling={Button.Styling.Flat}
                testId="van-offer-navigation-close"
                aria={{ 'aria-label': translate('a11y.actions.close') }}
                onClick={() => onClose(ClickAdEventTarget.CloseButton)}
              />
            }
          />
          <Spacer size={Spacer.Size.Large} />
          <Container paddingLeft={Spacer.Size.Large} paddingRight={Spacer.Size.Large}>
            <div className="van-offer-container">
              <div data-testid="van-offer-slot" id="van-offer-slot">
                <iframe
                  ref={iframeRef}
                  key={placementId || currentPlacementIndex}
                  id="van-offer-iframe"
                  title="van-offer-iframe"
                  width={creative.size.width}
                  height={creative.size.height}
                  className="van-offer-iframe"
                  srcDoc={iframeContent}
                  style={{
                    opacity: isIframeVisible ? 1 : 0,
                    transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
                  }}
                />
              </div>
              <Spacer size={Spacer.Size.XLarge} />
              <div style={{ width: creative.size.width }}>
                <Text
                  as="h2"
                  type={Text.Type.SectionHeading}
                  text={translateMulti('title', { count: offerCount }, { count: offerCount })}
                  alignment={Text.Alignment.Center}
                  width={Text.Width.Parent}
                  testId="van-offer-title"
                />
                <Spacer size={Spacer.Size.Small} />
                <Text
                  as="p"
                  text={
                    isMultipleOffers
                      ? translateMulti(`subtitle.${subtitleKey}`)
                      : translate('subtitle')
                  }
                  type={Text.Type.Subtitle}
                  alignment={Text.Alignment.Center}
                  width={Text.Width.Parent}
                  testId="van-offer-subtitle"
                />
                <Spacer size={Spacer.Size.XLarge} />
                <Button
                  text={translate('button')}
                  iconPosition={Button.IconPosition.Right}
                  iconName={Redirect16}
                  styling={Button.Styling.Filled}
                  size={Button.Size.Medium}
                  theme="primary"
                  onClick={onOfferButtonClick}
                  testId="van-offer-claim-button"
                />
                {isMultipleOffers && (
                  <>
                    <Spacer size={Spacer.Size.Medium} />
                    <Button
                      text={translateMulti('button.secondary')}
                      iconPosition={Button.IconPosition.Right}
                      styling={Button.Styling.Outlined}
                      size={Button.Size.Medium}
                      theme="primary"
                      onClick={onNextOffer}
                      testId="van-offer-secondary-button"
                    />
                  </>
                )}

                <Spacer size={Spacer.Size.XLarge} />
                <div className="van-multi-offer-info-container">
                  <div>
                    <Cell
                      styling={Cell.Styling.Tight}
                      clickable
                      body={
                        <div className="van-multi-offer-info">
                          <Icon name={InfoCircle16} color={ColorCode.GreyscaleLevel2} />
                          <AdInfo underline />
                        </div>
                      }
                      onClick={onAdInfoClick}
                      testId="ad-info-redirection-button"
                    />
                  </div>
                  {isMultipleOffers && (
                    <DotsIndicator
                      currentIndex={currentPlacementIndex}
                      totalItems={placements.length}
                    />
                  )}
                  <div>
                    <Cell
                      styling={Cell.Styling.Tight}
                      clickable
                      body={
                        <div className="van-multi-offer-info-text">
                          <Text
                            as="p"
                            type={Text.Type.Caption}
                            theme="muted"
                            text={translate('privacy_policy')}
                            underline
                            alignment={Text.Alignment.Right}
                          />
                        </div>
                      }
                      onClick={onPrivacyPolicyClick}
                      testId="privacy-policy-redirection-button"
                    />
                  </div>
                </div>
                <Spacer size={Spacer.Size.XLarge} />
              </div>
            </div>
          </Container>
        </div>
      </div>
    </Dialog>
  )
}

export default VanMultiOfferContent
